import type { AnyNeighbourhoodTemplate } from "./contracts";

export type NeighbourhoodValidationIssue = {
  severity: "error" | "warning";
  code: string;
  path: string;
  message: string;
};

export function ringSeparation(a: number, b: number) {
  const direct = Math.abs(a - b);
  return Math.min(direct, 12 - direct);
}

export function validateNeighbourhoodTemplate(template: AnyNeighbourhoodTemplate) {
  const issues: NeighbourhoodValidationIssue[] = [];
  const error = (code: string, path: string, message: string) => issues.push({ severity: "error", code, path, message });
  const castles = template.locations.filter((location) => location.kind === "castle");
  const ringIndices = castles.map((location) => location.castleRingIndex).filter((value): value is number => value !== null).sort((a, b) => a - b);
  if (castles.length !== 12 || ringIndices.join(",") !== "1,2,3,4,5,6,7,8,9,10,11,12")
    error("NEIGHBOURHOOD_CASTLE_RING", "locations", "Template requires exactly twelve Castle locations with ring indices 1–12.");
  if (template.locations.filter((location) => location.kind === "threat").length !== 1)
    error("NEIGHBOURHOOD_THREAT_ANCHOR", "locations", "Template requires exactly one central Threat anchor.");
  if (template.locations.filter((location) => location.kind === "resource").length === 0)
    error("NEIGHBOURHOOD_RESOURCE_SLOTS", "locations", "Template requires authored resource spawn locations.");
  if (template.locations.filter((location) => location.kind === "hunt").length === 0)
    error("NEIGHBOURHOOD_HUNT_SLOTS", "locations", "Template requires authored Hunt locations.");
  if (template.sectors.length === 0) error("NEIGHBOURHOOD_SECTORS", "sectors", "Template requires authored sectors.");
  if (!template.backgroundAssetId) error("NEIGHBOURHOOD_BACKGROUND", "backgroundAssetId", "Production template requires a background Asset.");
  const locationIds = new Set(template.locations.map((location) => location.id));
  const bandIds = new Set(template.travelBands.map((band) => band.id));
  for (const route of template.routes) {
    if (!locationIds.has(route.originLocationId) || !locationIds.has(route.destinationLocationId))
      error("NEIGHBOURHOOD_ROUTE_ENDPOINT", `routes.${route.key}`, "Route endpoints must belong to the same template.");
    if (route.travelBandId && !bandIds.has(route.travelBandId))
      error("NEIGHBOURHOOD_ROUTE_BAND", `routes.${route.key}.travelBandId`, "Route travel band must belong to the same template.");
  }
  if (template.routes.length === 0) error("NEIGHBOURHOOD_ROUTES", "routes", "Template requires authored logical routes.");
  for (const location of template.locations) {
    if (location.x > template.designWidth || location.y > template.designHeight)
      error("NEIGHBOURHOOD_LOCATION_BOUNDS", `locations.${location.key}`, "Location must stay inside the declared design space.");
  }
  if (template.contractVersion !== 1) for (const location of template.locations) {
    if (location.kind === "resource" && location.allowedResourceDefinitionIds.length === 0)
      error("NEIGHBOURHOOD_RESOURCE_ELIGIBILITY", `locations.${location.key}.allowedResourceDefinitionIds`, "Resource location requires at least one explicitly allowed Resource Node definition.");
    if (location.kind === "hunt" && location.allowedHuntDefinitionIds.length === 0)
      error("NEIGHBOURHOOD_HUNT_ELIGIBILITY", `locations.${location.key}.allowedHuntDefinitionIds`, "Hunt location requires at least one explicitly allowed Hunt definition.");
  }
  if (template.contractVersion === 3) {
    if (template.coordinateSpaceVersion !== 3 || template.designWidth !== 4096 || template.designHeight !== 4096)
      error("NEIGHBOURHOOD_WORLD_COORDINATES", "coordinateSpaceVersion", "Visual authoring requires the canonical 4096×4096 world coordinate space.");
    if (template.defaultCameraCenterX < 0 || template.defaultCameraCenterX > 4096 || template.defaultCameraCenterY < 0 || template.defaultCameraCenterY > 4096)
      error("NEIGHBOURHOOD_DEFAULT_CAMERA", "defaultCamera", "The saved player camera centre must remain inside the world.");
  }
  if (template.productionReady && template.importedLegacyDraft)
    error("NEIGHBOURHOOD_IMPORTED_DRAFT", "productionReady", "An imported legacy draft cannot be production-ready.");
  if (template.productionReady) {
    for (const route of template.routes)
      if (route.durationSeconds === null && !template.travelBands.find((band) => band.id === route.travelBandId)?.durationSeconds)
        error("NEIGHBOURHOOD_ROUTE_DURATION", `routes.${route.key}.durationSeconds`, "Production route requires an approved explicit duration or travel-band duration.");
  }
  return issues;
}

export function canPublishNeighbourhoodTemplate(template: AnyNeighbourhoodTemplate) {
  return template.productionReady && validateNeighbourhoodTemplate(template).every((issue) => issue.severity !== "error");
}
